/*
 * Bundle configurations section (sections/bundle-configurations.liquid).
 *
 * Two Alpine components:
 *
 *   xBundleConfigurations — the tab strip. Holds which configuration is showing.
 *
 *   xBundleConfiguration  — one configuration: its swatches, price and add to
 *     cart. Variant resolution happens here rather than through the PDP's
 *     fetch-the-section-again picker, because three products share one section
 *     id on this page and that picker addresses elements by section id alone.
 *
 * The variant map is emitted by Liquid as
 *   { "sage|sage|navy": [variantId, price, compareAtPrice, available] }
 * keyed on the option values, lower-cased and joined with a pipe, in option
 * order. Prices are in cents; `available` is 1 or 0; a compare-at price of 0
 * means there is none.
 */
if (!window.Eurus.loadedScript.has('bundle-configurations.js')) {
  window.Eurus.loadedScript.add('bundle-configurations.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xBundleConfigurations', (count) => ({
        active: 0,
        count: count || 1,
        init() {
          /* Selecting a configuration block in the theme editor should bring
             its tab forward, otherwise the merchant edits a panel they cannot
             see. */
          document.addEventListener('shopify:block:select', (event) => {
            const panels = Array.from(this.$root.querySelectorAll('[role="tabpanel"]'));
            const index = panels.indexOf(event.target);
            if (index !== -1) this.active = index;
          });
        },
        select(index) {
          this.active = index;
        },
        /* Left/right arrow keys walk the tab strip, per the tabs pattern. */
        move(step) {
          const next = (this.active + step + this.count) % this.count;
          this.active = next;
          this.$nextTick(() => {
            const tabs = this.$root.querySelectorAll('[role="tab"]');
            if (tabs[next]) tabs[next].focus();
          });
        }
      }));

      Alpine.data('xBundleConfiguration', (dataElementId) => ({
        rows: [],
        variants: {},
        selected: [],
        optionCount: 0,
        activePiece: null,
        variantId: null,
        available: false,
        priceText: '',
        compareText: '',
        buttonText: '',
        labels: {},
        currencyCode: false,

        init() {
          const dataElement = document.getElementById(dataElementId);
          try {
            this.variants = JSON.parse(dataElement.textContent).variants || {};
          } catch (error) {
            this.variants = {};
          }
          this.rows = Object.keys(this.variants).map((key) => ({
            values: key.split('|'),
            data: this.variants[key]
          }));

          this.currencyCode = this.$el.dataset.currencyCode === 'true';
          this.priceText = this.$el.dataset.initialPrice || '';
          this.labels = {
            addToCart: this.$el.dataset.labelAddToCart,
            soldOut: this.$el.dataset.labelSoldOut,
            unavailable: this.$el.dataset.labelUnavailable,
            choose: this.$el.dataset.labelChoose
          };

          const fieldsets = this.$el.querySelectorAll('[data-bundle-option-index]');
          this.optionCount = fieldsets.length;

          const initial = (this.$el.dataset.initialOptions || '').split('|');
          this.selected = new Array(this.optionCount).fill('');

          fieldsets.forEach((fieldset) => {
            const index = parseInt(fieldset.dataset.bundleOptionIndex, 10);
            const inputs = fieldset.querySelectorAll('input[type="radio"]');

            /* The picker snippet only pre-checks a value when Shopify itself
               resolved a selected variant, which never happens for a product
               referenced by a block. Restore the selection here instead. */
            inputs.forEach((input) => {
              const isInitial = initial[index] !== undefined && input.value === initial[index];
              input.checked = isInitial;
              if (isInitial) this.selected[index] = input.value;

              input.addEventListener('change', () => {
                if (!input.checked) return;
                this.selected[index] = input.value;
                this.sync();
              });
            });
          });

          this.sync();
        },

        sync() {
          const match = this.findVariant(this.selected);

          if (match) {
            this.variantId = match[0];
            this.available = match[3] === 1;
            this.priceText = this.formatMoney(match[1]);
            this.compareText = match[2] > match[1] ? this.formatMoney(match[2]) : '';
          } else {
            /* Nothing resolved — an incomplete selection, or a combination the
               product does not carry. Keep the last price on screen rather than
               blanking it, and let the button say why it cannot be bought. */
            this.variantId = null;
            this.available = false;
            this.compareText = '';
          }

          if (this.selected.filter(Boolean).length < this.optionCount) {
            this.buttonText = this.labels.choose;
          } else if (!match) {
            this.buttonText = this.labels.unavailable;
          } else if (!this.available) {
            this.buttonText = this.labels.soldOut;
          } else {
            this.buttonText = this.labels.addToCart;
          }

          this.refreshAvailability();
        },

        findVariant(values) {
          if (values.filter(Boolean).length < this.optionCount) return null;
          return this.variants[this.key(values)] || null;
        },

        key(values) {
          return values.map((value) => (value || '').toLowerCase()).join('|');
        },

        /* A value is offered when some variant that carries it, and matches
           everything else already picked, is in stock. Values that fail are
           given the theme's `disabled` class, which is what draws the sold-out
           slash across a swatch. */
        refreshAvailability() {
          this.$el.querySelectorAll('[data-bundle-option-index]').forEach((fieldset) => {
            const index = parseInt(fieldset.dataset.bundleOptionIndex, 10);
            fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
              const candidate = this.selected.slice();
              candidate[index] = input.value;
              input.classList.toggle('disabled', !this.hasAvailable(candidate));
            });
          });
        },

        hasAvailable(values) {
          const wanted = values.map((value) => (value || '').toLowerCase());
          return this.rows.some((row) => {
            if (row.data[3] !== 1) return false;
            return wanted.every((value, index) => value === '' || row.values[index] === value);
          });
        },

        formatMoney(cents) {
          const format = this.currencyCode
            ? window.Eurus.money_with_currency_format
            : window.Eurus.money_format;
          return Alpine.store('xHelper').formatMoney(cents, format);
        }
      }));
    });
  });
}
